
import React, { useCallback, useEffect, useMemo } from 'react'
import { type NodeProps, useReactFlow, useStore } from '@xyflow/react'
import { GalleryGroup } from '@/components/canvas/nodes/variants/GalleryGroup'
import type { GenieNodeState } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import type { HandleMeta } from '@/lib/canvas/types'
import { calculateGalleryDimensions } from '@/lib/canvas/utils/toReactFlowNode'
import { NODE_DIMENSIONS } from '@genieartinc/schemas'

export interface GalleryGroupNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
}

function GalleryGroupNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as GalleryGroupNodeData
  const { state: genieState } = nodeData
  const onStateChange = useNodeStateChange(id)
  const { updateNode } = useReactFlow()

  // Reactive child count — re-renders when children are added/removed
  const childCount = useStore(
    useCallback((s) => s.nodes.filter((n: { parentId?: string }) => n.parentId === id).length, [id]),
  )

  const columns = (genieState.columns as number) || 10
  const cellSize = (genieState.cellSize as number) || 200
  const collapsed = genieState.collapsed as boolean
  const sizeSource = genieState.sizeSource as string | undefined

  const dims = useMemo(() => {
    // A corner drag owns the box. Item count must not keep rewriting it.
    if (sizeSource === 'manual') return null
    if (!collapsed && childCount === 0) {
      return { width: NODE_DIMENSIONS.GalleryGroup.width, height: NODE_DIMENSIONS.GalleryGroup.height }
    }
    if (collapsed) return { width: columns * cellSize + 40, height: 40 }
    return calculateGalleryDimensions(childCount, columns, cellSize)
  }, [childCount, columns, cellSize, collapsed, sizeSource])

  // Sync React Flow node style with calculated dimensions.
  // Skip when the user has resized: content must not undo that box.
  useEffect(() => {
    if (!dims) return
    updateNode(id, (node) => ({
      style: { ...node.style, width: dims.width, height: dims.height },
    }))
  }, [dims, id, updateNode])

  const galleryProps = useMemo(
    () => ({
      id,
      title: genieState.title,
      selected: selected || genieState.selected,
      width: dims?.width,
      height: dims?.height,
      onStateChange,
      state: genieState,
      childCount,
      mediaType: genieState.mediaType as string | undefined,
    }),
    [id, genieState, selected, childCount, dims, onStateChange],
  )

  const handles: HandleMeta[] = nodeData.handles || STANDARD_HANDLES.GalleryGroup

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <GalleryGroup {...galleryProps} />
    </>
  )
}

export const GalleryGroupNode = React.memo(
  GalleryGroupNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as GalleryGroupNodeData
    const nextData = nextProps.data as unknown as GalleryGroupNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.seriesName === nextData.state.seriesName &&
      prevData.state.totalImages === nextData.state.totalImages &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.state.mediaType === nextData.state.mediaType &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

GalleryGroupNode.displayName = 'GalleryGroupNode'
